import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useOnline } from '@vueuse/core';
import { v4 as uuidv4 } from 'uuid';
import XLSX from 'xlsx';
import { MenuImportsService } from '@/api/services/menu-imports.service';
import { useGtag } from '@/composition/use-gtag';
import { useUi } from '@/composition/use-ui';
import {
  classifyFile,
  countsAsPage,
  csvWithSeparator,
  decodeCsvBytes,
  IMAGES_PER_READ,
  isCsvFile,
  limitCheck,
  limitMessageKey,
  PAGES_PER_READ,
  sheetRowsToText,
} from '@/domain/menu-import/capture-files';
import type { ICaptureCounts } from '@/domain/menu-import/capture-files';
import { photoWarning } from '@/domain/menu-import/photo-checks';
import type { TPhotoWarning } from '@/domain/menu-import/photo-checks';
import { PARTS_PER_PAGE_MAX } from '@/domain/menu-import/tall-pages';
import {
  getServerErrorCode,
  getServerErrorStatus,
} from '@/domain/reservations/reservation-errors';
import { menuImportErrorKey } from '@/domain/store-setup/setup-errors';
import { AlertType } from '@/interfaces/IAlert';
import type {
  IMenuImportPage,
  IMenuImportPageFields,
  TMenuImportPageOrigin,
} from '@/interfaces/IMenuImport';
import { readBlobAsArrayBuffer } from '@/lib/menu-capture/capture-dom';
import {
  ImageUnreadableError,
  processCapturePhoto,
} from '@/lib/menu-capture/image-processor';
import type { IProcessedPart } from '@/lib/menu-capture/image-processor';
import {
  PdfUnreadableError,
  rasterizePdf,
} from '@/lib/menu-capture/pdf-raster';
import type { IPdfPageImage } from '@/lib/menu-capture/pdf-raster';
import { i18n } from '@/plugins/i18n';
import type { IMenuImportSession } from './menu-import-context';
import { isNetworkError } from './use-menu-import-session';

/** Long edge of an uploaded photo (D12). */
export const CAPTURE_PHOTO_LONG_EDGE = 2560;
/**
 * One upload at a time, in the tray's order: the API numbers a page when its
 * upload lands and the read follows that order, so parallel uploads could
 * store a slow page after later ones (a section continued on the next page
 * would then be misplaced). For the same reason a page whose upload failed
 * after its retries holds the pages after it until it is retried or removed.
 */
export const CAPTURE_MAX_UPLOADS = 1;
/** Waits before each retry of a failed upload; then the page is `failed`. */
export const CAPTURE_UPLOAD_RETRY_MS: readonly number[] = [1000, 3000, 9000];
/** The server's `originName` limit. */
const ORIGIN_NAME_MAX = 120;

export type TCaptureEntryKind = 'photo' | 'pdf' | 'sheet' | 'paste';
export type TCaptureEntryState =
  | 'processing'
  | 'uploading'
  | 'ready'
  | 'failed';
export type TCaptureOrigin = 'camera' | 'gallery';

/** One page of the tray while it is prepared and uploaded on this device. */
export interface ICaptureEntry {
  localId: string;
  clientPageId: string;
  kind: TCaptureEntryKind;
  /** Where the page came from and its file: what groups the parts of a very tall page. */
  origin: TMenuImportPageOrigin;
  originName: string | null;
  /** Page number inside the PDF. */
  originPage: number | null;
  /** 1-based part of a very tall page read in parts; null for a whole page. */
  originPart: number | null;
  /** The first part of a very tall page still being cut: its page is not complete yet. */
  cutting: boolean;
  /** The first part of a very tall page cut short (its 40 parts, or the read's images): only its beginning is read. */
  cut: boolean;
  /** Object URL of the processed JPEG (photos and PDF pages). */
  previewUrl: string | null;
  state: TCaptureEntryState;
  warning: TPhotoWarning;
  /** «Usar igual» hid the warning. */
  warningDismissed: boolean;
  /** Upload progress, 0..1. */
  progress: number;
  /** The session page once uploaded. */
  pageId: string | null;
  /** i18n key of the failure (state `failed`). */
  errorKey: string | null;
}

export interface IUseMenuCapture {
  entries: Ref<ICaptureEntry[]>;
  /** Something is still being prepared or uploaded. */
  busy: ComputedRef<boolean>;
  addFiles(files: File[], origin: TCaptureOrigin): void;
  /** «Sacar de nuevo»: the new photo takes the place of that page. */
  retake(localId: string, file: File): Promise<void>;
  useAnyway(localId: string): void;
  /** «Quitar» on a local entry (an uploaded page is deleted on the server). */
  remove(localId: string): Promise<boolean>;
  /** «Quitar» on an uploaded page without a local entry (uploaded from another device or before a reload). */
  removePage(pageId: string): Promise<boolean>;
  /** «Reintentar» on a failed upload. */
  retry(localId: string): void;
  /**
   * One text page (pasted text or a spreadsheet), resolved once uploaded. A
   * pasted text that failed leaves no tile (it is still in the paste card)
   * and sending the same text again reuses its `clientPageId`, so it can
   * never be added twice.
   */
  addText(
    text: string,
    origin: 'paste' | 'sheet',
    name: string | null
  ): Promise<TAddTextOutcome>;
}

/** `added`: the page; `failed`: not added, with the copy to show (null when already told). */
export type TAddTextOutcome =
  | { status: 'added'; page: IMenuImportPage }
  | { status: 'failed'; errorKey: string | null };

/** The page the owner added: one record, or the records of the parts of a very tall page. */
interface ISourcePage {
  /** The owner removed it: its parts not cut yet are left out. */
  removed: boolean;
}

/** A page of this device: the exposed entry plus what its upload needs. */
interface ICaptureRecord {
  localId: string;
  clientPageId: string;
  kind: TCaptureEntryKind;
  sourcePage: ISourcePage;
  origin: TMenuImportPageOrigin;
  originName: string | null;
  originPage: number | null;
  originPart: number | null;
  /** The processed JPEG, kept until it is uploaded. */
  blob: Blob | null;
  /** The text of a text page, kept until it is uploaded. */
  text: string | null;
  warning: TPhotoWarning;
  previewUrl: string | null;
  pageId: string | null;
  attempts: number;
  removed: boolean;
  /**
   * The next page of a PDF being rendered: its pages go in before it, and the
   * pages added after the PDF wait behind it. Never uploaded, not counted in
   * the limits; removing it stops the PDF.
   */
  placeholder: boolean;
  /** Counted in the session's `pending` while processing or uploading. */
  release: () => void;
  settle: (page: IMenuImportPage | null) => void;
  done: Promise<IMenuImportPage | null>;
}

interface ICaptureRecordInit {
  kind: TCaptureEntryKind;
  origin: TMenuImportPageOrigin;
  originName: string | null;
  originPage: number | null;
  originPart: number | null;
  state: TCaptureEntryState;
  /** The page it is a part of (the parts of a very tall page share it). */
  sourcePage?: ISourcePage;
}

/**
 * Thrown from a page or part callback to stop rendering: the session closed,
 * or the read is full of pages or of images (the rest is left out).
 */
class CaptureStopped extends Error {
  constructor(readonly reason: 'closed' | 'pages' | 'images') {
    super(`CAPTURE_${reason.toUpperCase()}`);
    this.name = 'CaptureStopped';
  }
}

const FULL_MESSAGE_KEYS: Readonly<Record<'pages' | 'images', string>> = {
  pages: limitMessageKey('tooManyPages'),
  images: limitMessageKey('tooManyImages'),
};

/**
 * The file name as the API stores it (its `sanitizeOriginName`): the parts of
 * a page are grouped by it, so a part not uploaded yet must carry the same.
 */
function originName(name: string | null | undefined): string | null {
  const base = (name ?? '').split(/[\\/]/).pop() ?? '';
  const clean = base
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, ORIGIN_NAME_MAX)
    .trim();
  return clean.length > 0 ? clean : null;
}

/**
 * Upload errors worth retrying: no answer, a server-side failure, or too many
 * uploads in a minute (the parts of a long page go one after another);
 * uploads are idempotent by `clientPageId`.
 */
function isRetryableUploadError(error: unknown): boolean {
  const status = getServerErrorStatus(error);
  return (
    isNetworkError(error) ||
    (status !== null && (status >= 500 || status === 429))
  );
}

/** The first sheet of a spreadsheet as TSV text (SheetJS is statically imported, D12). */
async function spreadsheetText(file: File): Promise<string> {
  const bytes = await readBlobAsArrayBuffer(file);
  // SheetJS reads CSV bytes as Latin-1 unless they start with a UTF-8 BOM
  // («Café» -> «CafÃ©»), so a CSV is decoded here and read as delimited text
  // with its values kept as written («1/2» is not a date).
  const workbook = isCsvFile(file)
    ? XLSX.read(csvWithSeparator(decodeCsvBytes(new Uint8Array(bytes))), {
        type: 'string',
        raw: true,
      })
    : XLSX.read(bytes, { type: 'array' });
  const firstName = workbook.SheetNames[0];
  const sheet = firstName ? workbook.Sheets[firstName] : undefined;
  if (!sheet) return '';
  return sheetRowsToText(
    XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false })
  );
}

/**
 * The capture tray of the menu source screen (plan §4.3, §10.4, D12): files
 * are prepared strictly one at a time (photos resized, PDF pages rendered,
 * spreadsheets read as text), pages upload one at a time in the order they
 * were added with 3 retries each, and uploads pause while offline.
 * The limits of a read are checked before anything is processed.
 */
export function useMenuCapture(session: IMenuImportSession): IUseMenuCapture {
  const ui = useUi();
  const gtag = useGtag();
  const online = useOnline();
  const t = (key: string) => String(i18n.t(key));

  const entries = shallowRef<ICaptureEntry[]>([]);
  const records = new Map<string, ICaptureRecord>();
  /** Files waiting to be prepared, plus the one being prepared. */
  const processingJobs = ref(0);
  let processQueue: (() => Promise<void>)[] = [];
  let processing = false;
  let uploadQueue: string[] = [];
  let inFlight = 0;
  /** A page whose upload failed after its retries: the pages after it wait for it. */
  let blockedBy: string | null = null;
  /** The last pasted text that failed to upload, with the page id it was sent as. */
  let failedPaste: { text: string; clientPageId: string } | null = null;
  /** Bumped when the session changes and on unmount: older work stops. */
  let epoch = 0;
  let disposed = false;
  const timers = new Set<ReturnType<typeof setTimeout>>();

  const busy = computed(
    () =>
      processingJobs.value > 0 ||
      entries.value.some(
        (entry) => entry.state === 'processing' || entry.state === 'uploading'
      )
  );

  //#region Entries
  function updateEntry(localId: string, patch: Partial<ICaptureEntry>): void {
    entries.value = entries.value.map((entry) =>
      entry.localId === localId ? { ...entry, ...patch } : entry
    );
  }

  function entryOf(
    record: ICaptureRecord,
    state: TCaptureEntryState
  ): ICaptureEntry {
    return {
      localId: record.localId,
      clientPageId: record.clientPageId,
      kind: record.kind,
      origin: record.origin,
      originName: record.originName,
      originPage: record.originPage,
      originPart: record.originPart,
      cutting: false,
      cut: false,
      previewUrl: record.previewUrl,
      state,
      warning: record.warning,
      warningDismissed: false,
      progress: 0,
      pageId: null,
      errorKey: null,
    };
  }

  function createRecord(
    init: ICaptureRecordInit,
    replaceLocalId: string | null = null,
    clientPageId: string = uuidv4()
  ): ICaptureRecord {
    let settle: (page: IMenuImportPage | null) => void = () => undefined;
    const done = new Promise<IMenuImportPage | null>((resolve) => {
      settle = resolve;
    });
    const record: ICaptureRecord = {
      localId: uuidv4(),
      clientPageId,
      kind: init.kind,
      sourcePage: init.sourcePage ?? { removed: false },
      origin: init.origin,
      originName: init.originName,
      originPage: init.originPage,
      originPart: init.originPart,
      blob: null,
      text: null,
      warning: 'none',
      previewUrl: null,
      pageId: null,
      attempts: 0,
      removed: false,
      placeholder: false,
      release: session.hold(),
      settle,
      done,
    };
    records.set(record.localId, record);
    const entry = entryOf(record, init.state);
    const index = replaceLocalId
      ? entries.value.findIndex((known) => known.localId === replaceLocalId)
      : -1;
    entries.value =
      index < 0
        ? [...entries.value, entry]
        : [
            ...entries.value.slice(0, index),
            entry,
            ...entries.value.slice(index),
          ];
    return record;
  }

  /** Moves the entry `localId` right after the entry `afterLocalId` (the parts of a page stay together). */
  function placeAfter(localId: string, afterLocalId: string): void {
    const entry = entries.value.find((known) => known.localId === localId);
    if (!entry) return;
    const rest = entries.value.filter((known) => known.localId !== localId);
    const index = rest.findIndex((known) => known.localId === afterLocalId);
    entries.value =
      index < 0
        ? [...rest, entry]
        : [...rest.slice(0, index + 1), entry, ...rest.slice(index + 1)];
  }

  /** Forgets a record locally: object URL revoked, entry gone, pending released. */
  function discard(record: ICaptureRecord): void {
    record.removed = true;
    records.delete(record.localId);
    uploadQueue = uploadQueue.filter((id) => id !== record.localId);
    if (blockedBy === record.localId) blockedBy = null;
    if (record.previewUrl) URL.revokeObjectURL(record.previewUrl);
    record.previewUrl = null;
    record.blob = null;
    record.release();
    record.settle(null);
    entries.value = entries.value.filter(
      (entry) => entry.localId !== record.localId
    );
    // The pages that waited for it (see `waitsForEarlier`) may go on.
    pumpUploads();
  }

  function isStale(record: ICaptureRecord, fromEpoch: number): boolean {
    return disposed || record.removed || fromEpoch !== epoch;
  }

  /**
   * Photos, pages and images not read yet: the session's uploaded pages plus
   * this device's pages not uploaded yet. A page read in parts is one photo
   * or page; each part is one image.
   */
  function unreadCounts(): ICaptureCounts {
    const unread = session.pages.value.filter(
      (page) => page.status === 'uploaded' && page.readNo === null
    );
    const local = [...records.values()].filter(
      (record) =>
        !record.removed && record.pageId === null && !record.placeholder
    );
    const pages = [...unread, ...local].filter(countsAsPage);
    return {
      photos:
        unread.filter(
          (page) =>
            countsAsPage(page) &&
            (page.origin === 'camera' || page.origin === 'gallery')
        ).length +
        local.filter(
          (record) => countsAsPage(record) && record.kind === 'photo'
        ).length,
      pages: pages.length,
      images: unread.length + local.length,
    };
  }
  //#endregion

  //#region Uploads
  function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        timers.delete(timer);
        resolve();
      }, ms);
      timers.add(timer);
    });
  }

  function fail(record: ICaptureRecord, errorKey: string): void {
    record.release();
    record.settle(null);
    updateEntry(record.localId, { state: 'failed', errorKey, progress: 0 });
  }

  /** The copy of an upload that failed without a mapped code: a photo, or a text page. */
  function uploadFailedKey(record: ICaptureRecord): string {
    return record.text !== null
      ? 'menuImport.errors.generic'
      : 'menuImport.errors.uploadFailed';
  }

  function succeed(record: ICaptureRecord, page: IMenuImportPage): void {
    record.pageId = page.pageId;
    record.blob = null;
    record.text = null;
    session.upsertPage(page);
    record.release();
    record.settle(page);
    updateEntry(record.localId, {
      state: 'ready',
      pageId: page.pageId,
      progress: 1,
      errorKey: null,
    });
    gtag.event('menu_source_added', {
      kind: record.kind,
      warning: record.warning,
    });
  }

  function sendPage(
    sessionId: string,
    record: ICaptureRecord
  ): Promise<IMenuImportPage> {
    if (record.text !== null) {
      const origin = record.origin === 'sheet' ? 'sheet' : 'paste';
      return MenuImportsService.addText(sessionId, {
        clientPageId: record.clientPageId,
        origin,
        originName: record.originName,
        text: record.text,
      });
    }
    if (!record.blob) throw new Error('The page has nothing to upload.');
    const origin =
      record.origin === 'camera' || record.origin === 'gallery'
        ? record.origin
        : 'pdf';
    const fields: IMenuImportPageFields = {
      clientPageId: record.clientPageId,
      origin,
      originName: record.originName,
      originPage: record.originPage,
      originPart: record.originPart,
      clientWarning: record.warning === 'none' ? null : record.warning,
    };
    return MenuImportsService.uploadPage(
      sessionId,
      record.blob,
      fields,
      (fraction) => {
        if (!record.removed)
          updateEntry(record.localId, { progress: fraction });
      }
    );
  }

  /** Deletes a page that finished uploading after its entry was removed. */
  function deleteOrphan(sessionId: string, pageId: string): void {
    MenuImportsService.deletePage(sessionId, pageId).catch((error) =>
      console.warn('[menu-import] orphan page not removed', error)
    );
  }

  /** One page until it is uploaded, failed, removed, or put back to wait for the network. */
  async function upload(record: ICaptureRecord): Promise<void> {
    const fromEpoch = epoch;
    for (;;) {
      if (isStale(record, fromEpoch)) return;
      const sessionId = await session.ensureSession();
      if (isStale(record, fromEpoch)) return;
      if (!sessionId) {
        fail(record, uploadFailedKey(record));
        return;
      }
      try {
        const page = await sendPage(sessionId, record);
        if (disposed || fromEpoch !== epoch) return;
        if (record.removed) deleteOrphan(sessionId, page.pageId);
        else succeed(record, page);
        return;
      } catch (error) {
        if (isStale(record, fromEpoch)) return;
        if (!isRetryableUploadError(error)) {
          console.warn('[menu-import] page upload refused', error);
          fail(
            record,
            menuImportErrorKey(
              getServerErrorCode(error),
              getServerErrorStatus(error)
            ) ?? uploadFailedKey(record)
          );
          // A part uploaded after the next ones would split its page.
          if (record.originPart !== null) blockedBy = record.localId;
          return;
        }
        if (!online.value) {
          // Offline: the page waits (without using a retry) until the network is back.
          enqueue(record);
          return;
        }
        if (record.attempts >= CAPTURE_UPLOAD_RETRY_MS.length) {
          console.warn('[menu-import] page upload failed', error);
          fail(record, uploadFailedKey(record));
          blockedBy = record.localId;
          return;
        }
        await sleep(CAPTURE_UPLOAD_RETRY_MS[record.attempts++]);
      }
    }
  }

  /**
   * The API orders pages as they arrive, and a very tall page is its parts in
   * a row: a page waits while one before it in the tray is still being
   * prepared (a PDF still rendering included) or cut in parts, else it would
   * land before it or between its parts.
   */
  function waitsForEarlier(record: ICaptureRecord): boolean {
    for (const entry of entries.value) {
      if (entry.localId === record.localId) return false;
      const earlier = records.get(entry.localId);
      if (!earlier) continue;
      if (entry.state === 'processing') return true;
      if (entry.cutting && earlier.sourcePage !== record.sourcePage)
        return true;
    }
    return false;
  }

  function pumpUploads(): void {
    while (
      !disposed &&
      online.value &&
      blockedBy === null &&
      inFlight < CAPTURE_MAX_UPLOADS &&
      uploadQueue.length > 0
    ) {
      const record = records.get(uploadQueue[0]);
      if (!record || record.removed) {
        uploadQueue.shift();
        continue;
      }
      // The queue is in tray order: the pages after it wait too.
      if (waitsForEarlier(record)) return;
      uploadQueue.shift();
      inFlight++;
      const fromEpoch = epoch;
      void upload(record).finally(() => {
        if (fromEpoch !== epoch) return;
        inFlight--;
        pumpUploads();
      });
    }
  }

  /** Queues a page in the tray's order (a retried page goes before the pages after it). */
  function enqueue(record: ICaptureRecord): void {
    const order = new Map(
      entries.value.map((entry, index) => [entry.localId, index])
    );
    const at = order.get(record.localId) ?? Number.POSITIVE_INFINITY;
    const index = uploadQueue.findIndex(
      (id) => (order.get(id) ?? Number.POSITIVE_INFINITY) > at
    );
    uploadQueue =
      index < 0
        ? [...uploadQueue, record.localId]
        : [
            ...uploadQueue.slice(0, index),
            record.localId,
            ...uploadQueue.slice(index),
          ];
  }

  function queueUpload(record: ICaptureRecord): void {
    updateEntry(record.localId, {
      state: 'uploading',
      progress: 0,
      errorKey: null,
    });
    enqueue(record);
    pumpUploads();
  }
  //#endregion

  //#region Processing (strictly one file at a time)
  function enqueueProcess(task: () => Promise<void>): void {
    processingJobs.value++;
    processQueue.push(task);
    void pumpProcess();
  }

  async function pumpProcess(): Promise<void> {
    if (processing) return;
    processing = true;
    const fromEpoch = epoch;
    try {
      while (processQueue.length > 0 && !disposed && fromEpoch === epoch) {
        const task = processQueue.shift();
        try {
          if (task) await task();
        } finally {
          if (fromEpoch === epoch) processingJobs.value--;
        }
      }
    } finally {
      if (fromEpoch === epoch) processing = false;
    }
  }

  function showFileError(error: unknown): void {
    if (error instanceof ImageUnreadableError) {
      ui.showToast(AlertType.Error, t('menuImport.tray.fileError'));
      return;
    }
    console.error(error);
    ui.showToast(AlertType.Error, t('menuImport.errors.generic'));
  }

  /**
   * The object URL of a processed JPEG the tray shows: a whole page, or the
   * first part of a very tall one (the tile of all its parts).
   */
  function previewOf(blob: Blob, originPart: number | null): string | null {
    return countsAsPage({ originPart }) ? URL.createObjectURL(blob) : null;
  }

  /** A processed JPEG on its record, then its upload. */
  function readyToUpload(
    record: ICaptureRecord,
    blob: Blob,
    warning: TPhotoWarning,
    originPart: number | null
  ): void {
    record.blob = blob;
    record.originPart = originPart;
    record.warning = warning;
    record.previewUrl = previewOf(blob, originPart);
    updateEntry(record.localId, {
      previewUrl: record.previewUrl,
      warning: record.warning,
      originPart,
    });
    queueUpload(record);
  }

  /**
   * A photo, or the parts of a very tall one (a long screenshot): the first
   * part is the photo's record, each next part gets its own record right
   * after the previous one (the tray shows them as one page). Parts stop when
   * the owner removes the page or at the read's images limit. The session is
   * held while a photo is cut in parts: leaving then loses the rest.
   */
  async function processPhoto(
    record: ICaptureRecord,
    file: File
  ): Promise<void> {
    const fromEpoch = epoch;
    const stopped = () =>
      disposed || fromEpoch !== epoch || record.sourcePage.removed;
    let previous = record;
    /** The hold of a photo cut in parts (at most one). */
    const holds: (() => void)[] = [];
    const onPart = async (part: IProcessedPart): Promise<void> => {
      if (stopped()) throw new CaptureStopped('closed');
      if (part.part !== null && holds.length === 0) holds.push(session.hold());
      if (countsAsPage({ originPart: part.part })) {
        readyToUpload(record, part.blob, 'none', part.part);
        if (part.part !== null) updateEntry(record.localId, { cutting: true });
        return;
      }
      if (unreadCounts().images >= IMAGES_PER_READ)
        throw new CaptureStopped('images');
      const next = createRecord({
        kind: 'photo',
        origin: record.origin,
        originName: record.originName,
        originPage: null,
        originPart: part.part,
        state: 'uploading',
        sourcePage: record.sourcePage,
      });
      placeAfter(next.localId, previous.localId);
      previous = next;
      readyToUpload(next, part.blob, 'none', part.part);
    };
    try {
      const outcome = await processCapturePhoto(
        file,
        CAPTURE_PHOTO_LONG_EDGE,
        onPart,
        PARTS_PER_PAGE_MAX
      );
      if (isStale(record, fromEpoch)) return;
      if (outcome.kind === 'image')
        readyToUpload(
          record,
          outcome.image.blob,
          photoWarning(outcome.image.analysis, outcome.image.shortSide),
          null
        );
      else if (outcome.result.truncated) {
        updateEntry(record.localId, { cut: true });
        ui.showToast(AlertType.Error, t('menuImport.tray.longPageCut'));
      }
    } catch (error) {
      if (stopped()) return;
      if (error instanceof CaptureStopped) {
        if (error.reason !== 'closed')
          ui.showToast(AlertType.Error, t(FULL_MESSAGE_KEYS[error.reason]));
        // The read's images ran out after its first part: only its beginning is read.
        if (error.reason === 'images')
          updateEntry(record.localId, { cut: true });
        return;
      }
      // Parts already on their way stay; a photo with nothing ready goes.
      if (record.blob === null && record.pageId === null) discard(record);
      showFileError(error);
    } finally {
      holds.forEach((release) => release());
      if (records.has(record.localId)) {
        updateEntry(record.localId, { cutting: false });
        pumpUploads();
      }
    }
  }

  async function processSheet(
    record: ICaptureRecord,
    file: File
  ): Promise<void> {
    const fromEpoch = epoch;
    let text = '';
    try {
      text = await spreadsheetText(file);
    } catch (error) {
      console.warn('[menu-import] spreadsheet not readable', error);
    }
    if (isStale(record, fromEpoch)) return;
    if (text.trim().length < 3) {
      discard(record);
      ui.showToast(AlertType.Error, t('menuImport.tray.fileError'));
      return;
    }
    record.text = text;
    queueUpload(record);
  }

  /**
   * A PDF, one page (or part of a very tall page) at a time. Its pages go in
   * the tray before `placeholder`, the next page being prepared, so what is
   * added meanwhile stays (and uploads) after the whole PDF; removing the
   * placeholder stops the PDF.
   */
  async function processPdf(
    file: File,
    placeholder: ICaptureRecord
  ): Promise<void> {
    const fromEpoch = epoch;
    if (isStale(placeholder, fromEpoch)) return;
    const counts = unreadCounts();
    const remaining = PAGES_PER_READ - counts.pages;
    if (remaining <= 0 || counts.images >= IMAGES_PER_READ) {
      discard(placeholder);
      ui.showToast(
        AlertType.Error,
        t(FULL_MESSAGE_KEYS[remaining <= 0 ? 'pages' : 'images'])
      );
      return;
    }
    /** The page being cut in parts: the owner may remove it meanwhile. */
    let sourcePage: ISourcePage = { removed: false };
    /** The first part of the page being cut, shown as still in progress. */
    let cutting: string | null = null;
    const cutDone = () => {
      if (cutting !== null && records.has(cutting)) {
        updateEntry(cutting, { cutting: false });
        pumpUploads();
      }
      cutting = null;
    };
    const onPage = async (image: IPdfPageImage): Promise<void> => {
      if (isStale(placeholder, fromEpoch)) throw new CaptureStopped('closed');
      if (countsAsPage({ originPart: image.part })) {
        cutDone();
        sourcePage = { removed: false };
      }
      // The rest of a long page the owner removed is left out, not the next pages.
      else if (sourcePage.removed) return;
      // Photos added while the PDF renders count too. The later parts of a
      // very tall page are not pages, only images.
      const now = unreadCounts();
      if (
        countsAsPage({ originPart: image.part }) &&
        now.pages >= PAGES_PER_READ
      )
        throw new CaptureStopped('pages');
      if (now.images >= IMAGES_PER_READ) {
        // The read's images ran out in the middle of a long page.
        if (!countsAsPage({ originPart: image.part }) && cutting !== null)
          updateEntry(cutting, { cut: true });
        throw new CaptureStopped('images');
      }
      const record = createRecord(
        {
          kind: 'pdf',
          origin: 'pdf',
          originName: originName(file.name),
          originPage: image.pageNumber,
          originPart: image.part,
          state: 'uploading',
          sourcePage,
        },
        placeholder.localId
      );
      record.blob = image.blob;
      record.previewUrl = previewOf(image.blob, image.part);
      updateEntry(record.localId, {
        previewUrl: record.previewUrl,
        cutting: image.part === 1,
      });
      if (image.part === 1) cutting = record.localId;
      queueUpload(record);
    };
    // Leaving while the PDF renders loses its remaining pages: the leave guard asks.
    const release = session.hold();
    try {
      const result = await rasterizePdf(
        file,
        onPage,
        remaining,
        PARTS_PER_PAGE_MAX,
        () => {
          if (cutting !== null && records.has(cutting))
            updateEntry(cutting, { cut: true });
        }
      );
      if (!disposed && fromEpoch === epoch) {
        if (result.truncated)
          ui.showToast(AlertType.Error, t('menuImport.tray.tooManyPages'));
        else if (result.partsTruncated)
          ui.showToast(AlertType.Error, t('menuImport.tray.longPageCut'));
      }
    } catch (error) {
      if (disposed || fromEpoch !== epoch) return;
      if (error instanceof CaptureStopped) {
        if (error.reason !== 'closed')
          ui.showToast(AlertType.Error, t(FULL_MESSAGE_KEYS[error.reason]));
        return;
      }
      if (error instanceof PdfUnreadableError) {
        ui.showToast(AlertType.Error, t('menuImport.tray.pdfError'));
        return;
      }
      console.error(error);
      ui.showToast(AlertType.Error, t('menuImport.errors.generic'));
    } finally {
      if (!disposed && fromEpoch === epoch) {
        cutDone();
        if (records.has(placeholder.localId)) discard(placeholder);
      }
      release();
    }
  }
  //#endregion

  //#region Actions
  function addFiles(files: File[], origin: TCaptureOrigin): void {
    const shown = new Set<string>();
    const toastOnce = (key: string) => {
      if (shown.has(key)) return;
      shown.add(key);
      ui.showToast(AlertType.Error, t(key));
    };
    if (!online.value && files.length > 0) toastOnce('menuImport.tray.offline');

    for (const file of files) {
      const kind = classifyFile(file);
      if (kind === 'unsupported') {
        toastOnce('menuImport.tray.fileError');
        continue;
      }
      const isPhoto = kind === 'photo' || kind === 'heic';
      const check = limitCheck(unreadCounts(), {
        photos: isPhoto ? 1 : 0,
        pages: 1,
        images: 1,
      });
      if (check !== 'ok') {
        toastOnce(limitMessageKey(check));
        continue;
      }
      if (kind === 'pdf') {
        // Its place in the tray right away: its pages go in before it as they render.
        const placeholder = createRecord({
          kind: 'pdf',
          origin: 'pdf',
          originName: originName(file.name),
          originPage: null,
          originPart: null,
          state: 'processing',
        });
        placeholder.placeholder = true;
        enqueueProcess(() => processPdf(file, placeholder));
        continue;
      }
      const record = createRecord({
        kind: isPhoto ? 'photo' : 'sheet',
        origin: isPhoto ? origin : 'sheet',
        originName: originName(file.name),
        originPage: null,
        originPart: null,
        state: 'processing',
      });
      enqueueProcess(() =>
        isPhoto ? processPhoto(record, file) : processSheet(record, file)
      );
    }
  }

  async function retake(localId: string, file: File): Promise<void> {
    const previous = records.get(localId);
    if (!previous) return;
    const kind = classifyFile(file);
    if (kind !== 'photo' && kind !== 'heic') {
      ui.showToast(AlertType.Error, t('menuImport.tray.fileError'));
      return;
    }
    const origin: TCaptureOrigin =
      previous.origin === 'gallery' ? 'gallery' : 'camera';
    const record = createRecord(
      {
        kind: 'photo',
        origin,
        originName: originName(file.name),
        originPage: null,
        originPart: null,
        state: 'processing',
      },
      localId
    );
    enqueueProcess(() => processPhoto(record, file));
    await remove(localId);
  }

  function useAnyway(localId: string): void {
    updateEntry(localId, { warningDismissed: true });
  }

  async function removePage(pageId: string): Promise<boolean> {
    const sessionId = session.sessionId.value;
    if (!sessionId) return false;
    const fromEpoch = epoch;
    const done = await session.run(async () => {
      await MenuImportsService.deletePage(sessionId, pageId);
      return true;
    }, 'menuImport.errors.generic');
    if (!done) {
      // Most likely read meanwhile (PAGE_ALREADY_READ): show the current pages.
      void session.refresh();
      return false;
    }
    if (disposed || fromEpoch !== epoch) return false;
    session.removePage(pageId);
    const record = [...records.values()].find((r) => r.pageId === pageId);
    if (record) discard(record);
    pumpUploads();
    return true;
  }

  async function remove(localId: string): Promise<boolean> {
    const record = records.get(localId);
    // Already gone (a part that went with its page): nothing left to remove.
    if (!record) return true;
    // The page goes whole: the parts still being cut are not added and the
    // ones not uploaded yet go now (an upload in flight deletes its page
    // when it lands).
    const page = record.sourcePage;
    page.removed = true;
    for (const other of [...records.values()]) {
      if (other !== record && other.sourcePage === page && !other.pageId)
        discard(other);
    }
    if (!record.pageId) {
      discard(record);
      pumpUploads();
      return true;
    }
    return removePage(record.pageId);
  }

  function retry(localId: string): void {
    const record = records.get(localId);
    if (!record || record.pageId || (!record.blob && record.text === null))
      return;
    const entry = entries.value.find((known) => known.localId === localId);
    if (entry?.state !== 'failed') return;
    record.attempts = 0;
    record.release = session.hold();
    let settle: (page: IMenuImportPage | null) => void = () => undefined;
    record.done = new Promise((resolve) => {
      settle = resolve;
    });
    record.settle = settle;
    if (blockedBy === localId) blockedBy = null;
    queueUpload(record);
  }

  async function addText(
    text: string,
    origin: 'paste' | 'sheet',
    name: string | null
  ): Promise<TAddTextOutcome> {
    const trimmed = text.trim();
    if (trimmed.length < 3) return { status: 'failed', errorKey: null };
    const check = limitCheck(unreadCounts(), {
      photos: 0,
      pages: 1,
      images: 1,
    });
    if (check !== 'ok') {
      ui.showToast(AlertType.Error, t(limitMessageKey(check)));
      return { status: 'failed', errorKey: null };
    }
    if (!online.value)
      ui.showToast(AlertType.Error, t('menuImport.tray.offline'));
    // The same text sent again after a failure is the same page (the first try may have landed).
    const retried =
      origin === 'paste' && failedPaste?.text === trimmed
        ? failedPaste.clientPageId
        : undefined;
    const record = createRecord(
      {
        kind: origin === 'paste' ? 'paste' : 'sheet',
        origin,
        originName: originName(name),
        originPage: null,
        originPart: null,
        state: 'uploading',
      },
      null,
      retried
    );
    record.text = trimmed;
    queueUpload(record);
    const page = await record.done;
    if (page) {
      if (origin === 'paste') failedPaste = null;
      return { status: 'added', page };
    }
    const entry = entries.value.find(
      (known) => known.localId === record.localId
    );
    const errorKey = entry?.state === 'failed' ? entry.errorKey : null;
    // A pasted text stays in its card: no failed tile to retry it from twice.
    if (origin === 'paste' && entry) {
      failedPaste = { text: trimmed, clientPageId: record.clientPageId };
      discard(record);
    }
    return { status: 'failed', errorKey };
  }
  //#endregion

  //#region Lifecycle
  function discardAll(): void {
    epoch++;
    failedPaste = null;
    processQueue = [];
    processing = false;
    processingJobs.value = 0;
    uploadQueue = [];
    inFlight = 0;
    blockedBy = null;
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
    [...records.values()].forEach(discard);
  }

  // Uploads waiting for the network resume when it is back.
  watch(online, (isOnline) => {
    if (isOnline) pumpUploads();
  });

  // Pages being read leave the tray: their local copies are freed. A page
  // missing from the list is kept (a snapshot may predate its upload).
  watch(session.pages, (pages) => {
    const read = new Set(
      pages
        .filter((page) => page.status !== 'uploaded' || page.readNo !== null)
        .map((page) => page.pageId)
    );
    [...records.values()]
      .filter((record) => record.pageId !== null && read.has(record.pageId))
      .forEach(discard);
  });

  // Another session (another store) never inherits this tray.
  watch(session.sessionId, (sessionId, previousId) => {
    if (previousId && sessionId !== previousId) discardAll();
  });

  onScopeDispose(() => {
    discardAll();
    disposed = true;
  });
  //#endregion

  return {
    entries,
    busy,
    addFiles,
    retake,
    useAnyway,
    remove,
    removePage,
    retry,
    addText,
  };
}
